import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoreVert as MoreVertIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    padding: theme.spacing(1, 2),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 300,
  },
  phraseRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: "center",
  },
  phraseInput: {
    flex: 1,
  },
  phraseTypeSelect: {
    minWidth: 150,
  },
}));

const FlowCampaigns = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [flows, setFlows] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    flowId: "",
    phrase: [{ text: "", type: "contains" }],
    whatsappIds: [],
    status: true,
  });

  useEffect(() => {
    fetchCampaigns();
    fetchFlows();
  }, []);

  useEffect(() => {
    if (whatsApps && whatsApps.length > 0) {
      // WhatsApps ya están disponibles desde el contexto
    }
  }, [whatsApps]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/flowcampaigns");
      setCampaigns(data.records || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFlows = async () => {
    try {
      const { data } = await api.get("/flowbuilders", {
        params: { active: true },
      });
      setFlows(data.records || []);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSearch = (e) => {
    setSearchParam(e.target.value.toLowerCase());
    setPage(0);
  };

  const handleNewCampaign = () => {
    setFormData({
      name: "",
      flowId: "",
      phrase: [{ text: "", type: "contains" }],
      whatsappIds: [],
      status: true,
    });
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleMenuOpen = (e, campaign) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCampaign(null);
  };

  const handleMenuEdit = () => {
    if (selectedCampaign) {
      setFormData({
        name: selectedCampaign.name || "",
        flowId: selectedCampaign.flowId || "",
        phrase: selectedCampaign.phrase || [{ text: "", type: "contains" }],
        whatsappIds: selectedCampaign.whatsappIds || [],
        status: selectedCampaign.status !== undefined ? selectedCampaign.status : true,
      });
      setCampaignModalOpen(true);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    setConfirmModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteCampaign = async () => {
    if (!deletingCampaign) return;

    try {
      await api.delete(`/flowcampaigns/${deletingCampaign.id}`);
      toast.success(i18n.t("flowcampaigns.toasts.deleted") || "Campaña eliminada exitosamente");
      fetchCampaigns();
      setConfirmModalOpen(false);
      setDeletingCampaign(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveCampaign = async () => {
    try {
      // Validaciones
      if (!formData.name.trim()) {
        toast.error(i18n.t("flowcampaigns.validation.nameRequired") || "El nombre de la campaña es requerido");
        return;
      }

      if (!formData.flowId) {
        toast.error(i18n.t("flowcampaigns.validation.flowRequired") || "Debe seleccionar un flujo");
        return;
      }

      // Validar frases
      const validPhrases = formData.phrase.filter((p) => p.text.trim());
      if (validPhrases.length === 0) {
        toast.error(i18n.t("flowcampaigns.validation.phraseRequired") || "Debe proporcionar al menos una frase de activación");
        return;
      }

      // Validar WhatsApps
      if (formData.whatsappIds.length === 0) {
        toast.error(i18n.t("flowcampaigns.validation.whatsappRequired") || "Debe seleccionar al menos un WhatsApp");
        return;
      }

      const campaignData = {
        name: formData.name.trim(),
        flowId: parseInt(formData.flowId, 10),
        phrase: validPhrases.map((p) => ({
          text: p.text.trim(),
          type: p.type,
        })),
        whatsappIds: formData.whatsappIds,
        status: formData.status,
      };

      if (selectedCampaign) {
        // Actualizar
        await api.put(`/flowcampaigns/${selectedCampaign.id}`, campaignData);
        toast.success(i18n.t("flowcampaigns.toasts.updated") || "Campaña actualizada exitosamente");
      } else {
        // Crear
        await api.post("/flowcampaigns", campaignData);
        toast.success(i18n.t("flowcampaigns.toasts.created") || "Campaña creada exitosamente");
      }

      setCampaignModalOpen(false);
      setSelectedCampaign(null);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddPhrase = () => {
    setFormData({
      ...formData,
      phrase: [...formData.phrase, { text: "", type: "contains" }],
    });
  };

  const handleRemovePhrase = (index) => {
    const newPhrases = formData.phrase.filter((_, i) => i !== index);
    if (newPhrases.length === 0) {
      newPhrases.push({ text: "", type: "contains" });
    }
    setFormData({
      ...formData,
      phrase: newPhrases,
    });
  };

  const handlePhraseChange = (index, field, value) => {
    const newPhrases = [...formData.phrase];
    newPhrases[index] = { ...newPhrases[index], [field]: value };
    setFormData({
      ...formData,
      phrase: newPhrases,
    });
  };

  // Filtrar campañas por búsqueda
  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.name?.toLowerCase().includes(searchParam)
  );

  // Paginación
  const paginatedCampaigns = filteredCampaigns.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // WhatsApps conectados
  const connectedWhatsApps = whatsApps?.filter((w) => w.status === "CONNECTED") || [];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("flowcampaigns.title") || "Campañas de Flujo"}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewCampaign}
            startIcon={<AddIcon />}
          >
            {i18n.t("flowcampaigns.buttons.add") || "Nueva Campaña"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <TextField
            placeholder={i18n.t("flowcampaigns.searchPlaceholder") || "Buscar campañas..."}
            type="search"
            size="small"
            variant="outlined"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowcampaigns.table.name") || "Nombre"}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowcampaigns.table.flow") || "Flujo"}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowcampaigns.table.phrases") || "Frases"}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowcampaigns.table.whatsapps") || "WhatsApp(s)"}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowcampaigns.table.status") || "Estado"}
              </TableCell>
              <TableCell className={classes.customTableCell} align="center">
                {i18n.t("flowcampaigns.table.actions") || "Acciones"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={6} />}
            {!loading && paginatedCampaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchParam
                      ? i18n.t("flowcampaigns.noCampaigns") || "No se encontraron campañas"
                      : i18n.t("flowcampaigns.noCampaigns") || "No hay campañas creadas"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              paginatedCampaigns.map((campaign, index) => (
                <TableRow key={campaign?.id || `campaign-${index}`} hover>
                  <TableCell className={classes.customTableCell}>
                    {campaign?.name || "Sin nombre"}
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    {campaign?.flowName || `Flujo #${campaign?.flowId}`}
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    {campaign?.phrase?.length || 0} frase(s)
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    {campaign?.whatsappIds?.length || 0} WhatsApp(s)
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    <Chip
                      label={
                        campaign?.status
                          ? i18n.t("flowcampaigns.status.active") || "Activa"
                          : i18n.t("flowcampaigns.status.inactive") || "Inactiva"
                      }
                      color={campaign?.status ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={classes.customTableCell} align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, campaign)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredCampaigns.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          labelRowsPerPage={i18n.t("flowcampaigns.rowsPerPage") || "Filas por página:"}
        />
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuEdit}>
            <EditIcon style={{ marginRight: 8, fontSize: 18 }} />
            {i18n.t("flowcampaigns.actions.edit") || "Editar"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setDeletingCampaign(selectedCampaign);
              handleMenuDelete();
            }}
          >
            <DeleteOutlineIcon style={{ marginRight: 8, fontSize: 18 }} />
            {i18n.t("flowcampaigns.actions.delete") || "Eliminar"}
          </MenuItem>
        </Menu>
        <ConfirmationModal
          title={i18n.t("flowcampaigns.confirmationModal.deleteTitle") || "Eliminar Campaña"}
          open={confirmModalOpen}
          onClose={() => {
            setConfirmModalOpen(false);
            setDeletingCampaign(null);
          }}
          onConfirm={handleDeleteCampaign}
        >
          {i18n.t("flowcampaigns.confirmationModal.deleteMessage") ||
            "¿Está seguro que desea eliminar esta campaña?"}
        </ConfirmationModal>

        {/* Modal de Crear/Editar Campaña */}
        <Dialog
          open={campaignModalOpen}
          onClose={() => {
            setCampaignModalOpen(false);
            setSelectedCampaign(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedCampaign
              ? i18n.t("flowcampaigns.form.editTitle") || "Editar Campaña"
              : i18n.t("flowcampaigns.form.title") || "Nueva Campaña"}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <FormControl className={classes.formControl} fullWidth>
              <TextField
                label={i18n.t("flowcampaigns.form.name") || "Nombre de la Campaña"}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                variant="outlined"
                required
              />
            </FormControl>

            <FormControl className={classes.formControl} fullWidth variant="outlined">
              <InputLabel>{i18n.t("flowcampaigns.form.flow") || "Flujo Asociado"}</InputLabel>
              <Select
                value={formData.flowId}
                onChange={(e) => setFormData({ ...formData, flowId: e.target.value })}
                label={i18n.t("flowcampaigns.form.flow") || "Flujo Asociado"}
                required
              >
                <MenuItem value="">
                  <em>Seleccionar flujo</em>
                </MenuItem>
                {flows.map((flow) => (
                  <MenuItem key={flow.id} value={flow.id}>
                    {flow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("flowcampaigns.form.phrases") || "Frases de Activación"}
              </Typography>
              {formData.phrase.map((phrase, index) => (
                <Box key={index} className={classes.phraseRow}>
                  <TextField
                    className={classes.phraseInput}
                    placeholder="Texto de la frase"
                    value={phrase.text}
                    onChange={(e) =>
                      handlePhraseChange(index, "text", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                  />
                  <FormControl
                    className={classes.phraseTypeSelect}
                    variant="outlined"
                    size="small"
                  >
                    <Select
                      value={phrase.type}
                      onChange={(e) =>
                        handlePhraseChange(index, "type", e.target.value)
                      }
                    >
                      <MenuItem value="exact">
                        {i18n.t("flowcampaigns.form.phraseTypes.exact") || "Exacta"}
                      </MenuItem>
                      <MenuItem value="contains">
                        {i18n.t("flowcampaigns.form.phraseTypes.contains") || "Contiene"}
                      </MenuItem>
                      <MenuItem value="startsWith">
                        {i18n.t("flowcampaigns.form.phraseTypes.startsWith") || "Comienza con"}
                      </MenuItem>
                      <MenuItem value="endsWith">
                        {i18n.t("flowcampaigns.form.phraseTypes.endsWith") || "Termina con"}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePhrase(index)}
                    disabled={formData.phrase.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddPhrase}
              >
                {i18n.t("flowcampaigns.form.addPhrase") || "Agregar Frase"}
              </Button>
            </Box>

            <FormControl className={classes.formControl} fullWidth variant="outlined">
              <InputLabel>{i18n.t("flowcampaigns.form.whatsapps") || "WhatsApp(s)"}</InputLabel>
              <Select
                multiple
                value={formData.whatsappIds}
                onChange={(e) =>
                  setFormData({ ...formData, whatsappIds: e.target.value })
                }
                renderValue={(selected) =>
                  selected
                    .map(
                      (id) =>
                        connectedWhatsApps.find((w) => w.id === id)?.name || `WhatsApp #${id}`
                    )
                    .join(", ")
                }
                label={i18n.t("flowcampaigns.form.whatsapps") || "WhatsApp(s)"}
                required
              >
                {connectedWhatsApps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    <Checkbox
                      checked={formData.whatsappIds.indexOf(whatsapp.id) > -1}
                    />
                    <ListItemText primary={whatsapp.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.checked })
                  }
                />
              }
              label={i18n.t("flowcampaigns.form.status") || "Campaña Activa"}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCampaignModalOpen(false);
                setSelectedCampaign(null);
              }}
            >
              {i18n.t("flowcampaigns.form.cancel") || "Cancelar"}
            </Button>
            <Button onClick={handleSaveCampaign} color="primary" variant="contained">
              {selectedCampaign
                ? i18n.t("flowcampaigns.form.update") || "Actualizar"
                : i18n.t("flowcampaigns.form.save") || "Crear"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </MainContainer>
  );
};

export default FlowCampaigns;
